/**
 * "Sign in with your company" (LOGIN-TO-TENANT.md §5) — email first: the person types their work email, we find their
 * company's own sign-in (Entra, Okta, any SAML/OIDC) and send them straight there. Works for every provider connected
 * through federated_idps.json — no per-provider button.
 */
import React, { useState } from 'react';
import { lookupCompanySignIn, startHostedUISignIn } from '@stepflow/ui-shared/services/cognitoOAuth.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export default function CompanySsoSignIn({ apiBase = API_BASE_URL, onBeforeRedirect = null }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  if (!open) {
    return (
      <button type="button" data-testid="sso-open" onClick={() => setOpen(true)}
        className="w-full text-center text-sm font-medium text-gray-600 hover:text-gray-900">
        Sign in with your company (SSO)
      </button>
    );
  }
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    const providers = await lookupCompanySignIn(apiBase, email);
    if (!providers.length) {
      setMsg("Your company doesn't use single sign-on here — sign in with your email and password.");
      setBusy(false);
      return;
    }
    try {
      if (onBeforeRedirect) await onBeforeRedirect();
      await startHostedUISignIn(providers[0]);
    } catch (err) {
      setMsg((err && err.message) || "Couldn't start your company's sign-in.");
      setBusy(false);
    }
  };
  return (
    <form data-testid="sso-form" onSubmit={go} className="space-y-2">
      <input data-testid="sso-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com" aria-label="Work email"
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
      <button type="submit" data-testid="sso-continue" disabled={busy || !email.trim()}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
        {busy ? 'Finding your company…' : 'Continue with company sign-in'}
      </button>
      {msg ? <p data-testid="sso-msg" className="text-xs text-gray-600">{msg}</p> : null}
    </form>
  );
}
